// File: src/services/media/registry/firestoreRegistryResolver.ts
//
// Firestore Media Registry Resolver (provider="registry")
// ------------------------------------------------------
// Installs a registryResolver into resolveMedia via installResolveMedia().
//
// Default Firestore doc paths:
// - If ref.registry is provided:
//     /mediaRegistry/{registry}/items/{key}
// - Else:
//     /mediaRegistry/{key}
//
// Supported document shapes (recommended):
//   {
//     v: 1,
//     kind?: "audio"|"video"|"image"|"binary",
//     mime?: string,
//
//     // Option A: direct URL
//     url: "https://.../file.wav",
//
//     // Option B: indirect provider resolved via resolveMedia()
//     provider: "firebase"|"static"|"gcs"|"url",
//     path?: string,        // firebase/static
//     gsUri?: string,       // gcs
//     bucket?: string,      // gcs
//     object?: string       // gcs
//   }
//
// Notes:
// - No dynamic imports (keeps Vite transform simple)
// - Pass explicit firestore instance to avoid default app reliance
// - Registry resolution is cached + inflight-deduped
// - Installs a window hook: window.__oc_media.resolveRegistryToUrl(ref) → url|null
//
// Debugging helpers:
// - window.__oc_media.__lastRegistryResolve: last resolve attempt status
// - window.__oc_media.__lastRegistryMeta: last resolve metadata (path, projectId-ish)
//
// IMPORTANT: This resolver intentionally THROWS on missing keys so callers can
//            surface actionable errors (instead of silent null).

import { getFirestore, doc, getDoc, type Firestore } from "firebase/firestore";

import type {
  MediaKindV1,
  MediaRefV1,
  RegistryMediaRefV1,
} from "@/services/media/contracts/mediaRefV1";
import type { RegistryResolveResult } from "@/services/media/resolve/resolveMedia";

import { normKind, normStr } from "@/services/media/contracts/mediaRefV1";
import { installResolveMedia, resolveMedia } from "@/services/media/resolve/resolveMedia";

type NonRegistryMediaRefBase = {
  v: 1;
  kind: MediaKindV1;
  mime?: string;
  label?: string;
};

function buildNonRegistryMediaRefBase(
  kind: MediaKindV1,
  mime: string,
  label: string,
): NonRegistryMediaRefBase {
  return {
    v: 1,
    kind,
    ...(mime ? { mime } : {}),
    ...(label ? { label } : {}),
  };
}

/* ------------------------------------------------------------------ */
/* ✅ Window hook installer (used by resolveAudioBufferFromMediaRef)   */
/* ------------------------------------------------------------------ */

function installOcMediaHookResolveRegistryToUrl(
  resolveRegistryToUrl: (ref: RegistryMediaRefV1) => Promise<string | null>,
): () => void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;
  w.__oc_media = w.__oc_media || {};

  const prev = w.__oc_media.resolveRegistryToUrl;

  const handler = async (ref: any) => {
    try {
      const out = await resolveRegistryToUrl(ref as RegistryMediaRefV1);

      w.__oc_media.__lastRegistryResolve = {
        ok: true,
        at: Date.now(),
        ref,
        url: out ?? null,
      };

      return out ? String(out) : null;
    } catch (err) {
      w.__oc_media.__lastRegistryResolve = {
        ok: false,
        at: Date.now(),
        ref,
        error: String(err),
      };

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[mediaRegistry] resolveRegistryToUrl failed:", err);
      }
      return null;
    }
  };

  w.__oc_media.resolveRegistryToUrl = handler;

  return () => {
    try {
      if (w.__oc_media?.resolveRegistryToUrl === handler) {
        if (prev) w.__oc_media.resolveRegistryToUrl = prev;
        else delete w.__oc_media.resolveRegistryToUrl;
      }
    } catch {
      // ignore
    }
  };
}

export type FirestoreMediaRegistryDocV1 = {
  v?: number;
  kind?: MediaKindV1 | string;
  mime?: string;

  // Direct URL (fast path)
  url?: string;
  downloadUrl?: string;

  // Indirect provider
  provider?: "url" | "static" | "firebase" | "gcs" | string;
  path?: string;
  storagePath?: string;

  // gcs
  gsUri?: string;
  bucket?: string;
  object?: string;

  // Optional
  label?: string;
  public?: boolean;
  meta?: Record<string, any>;
};

function readDirectUrl(data: FirestoreMediaRegistryDocV1): string {
  return normStr(data.url ?? data.downloadUrl, "");
}

function readProviderPath(data: FirestoreMediaRegistryDocV1): string {
  return normStr(data.path ?? data.storagePath, "");
}

async function resolveDevCatalogRegistryFallback(
  ref: RegistryMediaRefV1,
  docPath: string,
): Promise<RegistryResolveResult | null> {
  if (!import.meta.env.DEV || ref.registry) return null;

  const { OMNICINE_DEV_ASSET_CATALOG } = await import("@/demo/omnicineDevAssetCatalog.data");
  const match = OMNICINE_DEV_ASSET_CATALOG.find((entry) => entry.previewAssetId === ref.key);
  if (!match) return null;

  return {
    url: match.mediaUrl,
    meta: {
      source: "devCatalog",
      mode: "direct",
      key: ref.key,
      registry: null,
      docPath,
      registryDoc: {
        key: ref.key,
        registry: null,
        docPath,
        v: 1,
        kind: match.kind,
        mime: "",
        label: match.label,
        provider: "url",
        url: match.mediaUrl,
        downloadUrl: "",
        path: "",
        storagePath: "",
        gsUri: "",
        bucket: "",
        object: "",
        meta: { fallback: "omnicineDevAssetCatalog" },
      },
      label: match.label,
    },
  };
}

export type FirestoreRegistryResolverOptions = {
  /** Provide Firestore instance to avoid relying on getFirestore() default app. */
  firestore?: Firestore;

  /** Base collection name. Default: "mediaRegistry" */
  collection?: string;

  /**
   * Override doc path mapping.
   * Return path segments suitable for firebase/firestore doc(firestore, ...segments).
   */
  docPathForRef?: (ref: RegistryMediaRefV1) => string[];

  /** Cache TTL in ms (default 10s). Set 0 to disable. */
  cacheTtlMs?: number;
};

type CacheEntry = { ts: number; value: RegistryResolveResult };
type InflightEntry = Promise<RegistryResolveResult>;

const _cache = new Map<string, CacheEntry>();
const _inflight = new Map<string, InflightEntry>();

function cacheKey(ref: RegistryMediaRefV1, collection: string): string {
  const ns = ref.registry ? `${ref.registry}::` : "";
  return `${collection}::${ns}${ref.key}`;
}

function defaultDocPathForRef(ref: RegistryMediaRefV1, collection: string): string[] {
  if (ref.registry) return [collection, ref.registry, "items", ref.key];
  return [collection, ref.key];
}

function toNonRegistryMediaRef(
  ref: RegistryMediaRefV1,
  docData: FirestoreMediaRegistryDocV1,
): MediaRefV1 {
  const kind = normKind(docData.kind, ref.kind);
  const mime = normStr(docData.mime, "");
  const label = normStr(docData.label, "");

  const provider = normStr(docData.provider, "").toLowerCase();
  const base = buildNonRegistryMediaRefBase(kind, mime, label);

  // If the doc has a url field but forgot provider, treat as url.
  // (direct URL fast-path is handled earlier; this is just for completeness)
  const directUrl = readDirectUrl(docData);

  if (!provider && directUrl) {
    return { ...base, provider: "url", url: directUrl };
  }

  if (provider === "url") {
    const url = directUrl;
    if (!url) {
      throw new Error(
        `Registry doc provider="url" requires "url" (key="${ref.key}")`,
      );
    }
    return { ...base, provider: "url", url };
  }

  if (provider === "static") {
    const path = readProviderPath(docData);
    if (!path) {
      throw new Error(
        `Registry doc provider="static" requires "path" (key="${ref.key}")`,
      );
    }
    return { ...base, provider: "static", path };
  }

  if (provider === "firebase") {
    const path = readProviderPath(docData);
    if (!path) {
      throw new Error(
        `Registry doc provider="firebase" requires "path" (key="${ref.key}")`,
      );
    }
    return { ...base, provider: "firebase", path };
  }

  if (provider === "gcs") {
    const gsUri = normStr(docData.gsUri, "");
    const bucket = normStr(docData.bucket, "");
    const object = normStr(docData.object, "");
    if (!gsUri && !(bucket && object)) {
      throw new Error(
        `Registry doc provider="gcs" requires gsUri OR (bucket+object) (key="${ref.key}")`,
      );
    }
    return {
      ...base,
      provider: "gcs",
      ...(gsUri ? { gsUri } : {}),
      ...(bucket ? { bucket } : {}),
      ...(object ? { object } : {}),
    };
  }

  throw new Error(
    `Unsupported registry doc provider="${provider || "(missing)"}" (key="${ref.key}")`,
  );
}

function stableMetaDoc(
  data: FirestoreMediaRegistryDocV1,
  ref: RegistryMediaRefV1,
  docPath: string,
) {
  return {
    key: ref.key,
    registry: ref.registry || null,
    docPath,
    v: Number.isFinite(Number((data as any).v)) ? Number((data as any).v) : 1,
    kind: normKind((data as any).kind, ref.kind),
    mime: normStr((data as any).mime, ""),
    label: normStr((data as any).label, ""),
    provider: normStr((data as any).provider, ""),
    url: readDirectUrl(data),
    downloadUrl: normStr((data as any).downloadUrl, ""),
    path: readProviderPath(data),
    storagePath: normStr((data as any).storagePath, ""),
    gsUri: normStr((data as any).gsUri, ""),
    bucket: normStr((data as any).bucket, ""),
    object: normStr((data as any).object, ""),
    meta: (data as any).meta ?? null,
  };
}

export function installFirestoreMediaRegistryResolver(
  opts: FirestoreRegistryResolverOptions = {},
): () => void {
  const collection = opts.collection || "mediaRegistry";
  const cacheTtlMs = Number.isFinite(opts.cacheTtlMs) ? Number(opts.cacheTtlMs) : 10_000;

  const registryResolver = async (
    ref: RegistryMediaRefV1,
  ): Promise<RegistryResolveResult> => {
    const ckey = cacheKey(ref, collection);

    // TTL cache
    if (cacheTtlMs > 0) {
      const hit = _cache.get(ckey);
      if (hit && Date.now() - hit.ts < cacheTtlMs) return hit.value;
    }

    // Inflight dedupe
    const inflight = _inflight.get(ckey);
    if (inflight) return inflight;

    const task = (async (): Promise<RegistryResolveResult> => {
      const firestore = opts.firestore ?? getFirestore();
      const path = opts.docPathForRef
        ? opts.docPathForRef(ref)
        : defaultDocPathForRef(ref, collection);

      const docPath = path.join("/");

      // Helpful debug metadata (works even if the resolver throws)
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w: any = window as any;
        if (w) {
          w.__oc_media = w.__oc_media || {};
          w.__oc_media.__lastRegistryMeta = {
            at: Date.now(),
            key: ref.key,
            registry: ref.registry || null,
            collection,
            docPath,
            // best-effort introspection (not guaranteed across SDK versions)
            firestoreHost:
              (firestore as any)?._delegate?._settings?.host ??
              (firestore as any)?._delegate?._settings?.host ??
              null,
          };
        }
      } catch {
        // ignore
      }

      const docRef =
        path.length === 2
          ? doc(firestore, path[0], path[1])
          : doc(firestore, path[0], path[1], path[2], path[3]);

      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        const fallback = await resolveDevCatalogRegistryFallback(ref, docPath);
        if (fallback) {
          if (cacheTtlMs > 0) _cache.set(ckey, { ts: Date.now(), value: fallback });
          return fallback;
        }

        throw new Error(
          `Registry key not found in Firestore (${docPath}) for key="${ref.key}"${
            ref.registry ? ` registry="${ref.registry}"` : ""
          }`,
        );
      }

      const data = snap.data() as FirestoreMediaRegistryDocV1;

      // Prefer doc kind if present, otherwise keep ref.kind.
      const resolvedKind = normKind(data.kind, ref.kind);
      const docMime = normStr(data.mime, "");
      const label = normStr(data.label, "");

      // Fast path: direct URL field with no provider needed (or provider=url)
      const directUrl = readDirectUrl(data);
      const directProvider = normStr(data.provider, "").toLowerCase();

      if (directUrl && (!directProvider || directProvider === "url")) {
        const out: RegistryResolveResult = {
          url: directUrl,
          ...(() => {
            const m =
              docMime ||
              (() => {
                const u = directUrl.toLowerCase();
                if (u.includes(".mp3")) return "audio/mpeg";
                if (u.includes(".wav")) return "audio/wav";
                if (u.includes(".m4a")) return "audio/mp4";
                if (u.includes(".ogg") || u.includes(".opus")) return "audio/ogg";
                if (u.includes(".mp4")) return "video/mp4";
                if (u.includes(".webm")) return "video/webm";
                if (u.includes(".png")) return "image/png";
                if (u.includes(".jpg") || u.includes(".jpeg")) return "image/jpeg";
                if (u.includes(".webp")) return "image/webp";
                return "";
              })();
            return m ? { mime: m } : {};
          })(),
          meta: {
            source: "firestore",
            mode: "direct",
            key: ref.key,
            registry: ref.registry || null,
            docPath,
            registryDoc: stableMetaDoc(data, ref, docPath),
            ...(label ? { label } : {}),
          },
        };

        if (cacheTtlMs > 0) _cache.set(ckey, { ts: Date.now(), value: out });
        return out;
      }

      // Indirect provider: normalize to MediaRefV1 then resolve via canonical resolveMedia()
      const nonRegistryRef = toNonRegistryMediaRef({ ...ref, kind: resolvedKind }, data);
      const resolved = await resolveMedia(nonRegistryRef);

      const out: RegistryResolveResult = {
        url: resolved.url,
        ...(resolved.mime ? { mime: resolved.mime } : {}),
        meta: {
          ...(resolved.meta || {}),
          source: "firestore",
          mode: `provider:${nonRegistryRef.provider}`,
          key: ref.key,
          registry: ref.registry || null,
          docPath,
          registryDoc: stableMetaDoc(data, ref, docPath),
          ...(label ? { label } : {}),
        },
      };

      if (cacheTtlMs > 0) _cache.set(ckey, { ts: Date.now(), value: out });
      return out;
    })();

    _inflight.set(ckey, task);

    try {
      return await task;
    } finally {
      _inflight.delete(ckey);
    }
  };

  /* ------------------------------------------------------------------ */
  /* ✅ helper used by resolveAudioBufferFromMediaRef() window hook      */
  /* ------------------------------------------------------------------ */

  const resolveRegistryToUrl = async (ref: RegistryMediaRefV1): Promise<string | null> => {
    const r = await registryResolver(ref);
    const url = normStr((r as any)?.url, "");

    // ✅ Best place to stamp rich debug meta: we have r.meta.registryDoc now.
    try {
      const w: any = typeof window !== "undefined" ? (window as any) : null;
      if (w) {
        w.__oc_media = w.__oc_media || {};
        const meta: any = (r as any)?.meta || {};

        w.__oc_media.__lastRegistryMeta = {
          at: Date.now(),
          key: ref?.key ?? null,
          registry: (ref as any)?.registry ?? null,
          collection,
          docPath: normStr(meta.docPath, meta.path || ""),
          // ✅ the missing field you want to inspect:
          registryDoc: meta.registryDoc ?? null,
          // helpful extras
          mode: meta.mode ?? null,
          source: meta.source ?? null,
          url: url || null,
        };
      }
    } catch {
      // ignore debug meta failures
    }

    return url ? url : null;
  };

  // Mirror to global so resolveMedia() can always find it, even across lazy chunks/routes.
  const g = globalThis as any;
  g.__oc_media_registry = g.__oc_media_registry || {};
  g.__oc_media_registry.registryResolver = registryResolver;

  // Install into canonical resolver pipeline
  const uninstallResolveMedia = installResolveMedia({ registryResolver });

  // ✅ Also install the window hook used by resolveAudioBufferFromMediaRef()
  const uninstallHook = installOcMediaHookResolveRegistryToUrl(resolveRegistryToUrl);

  return () => {
    try {
      uninstallHook();
    } catch {
      // ignore
    }
    try {
      uninstallResolveMedia();
    } catch {
      // ignore
    }
    try {
      const g2 = globalThis as any;
      if (g2.__oc_media_registry?.registryResolver === registryResolver) {
        delete g2.__oc_media_registry.registryResolver;
      }
    } catch {
      // ignore
    }
  };
}
